import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { SkeletonModule } from 'primeng/skeleton';
import { DialogModule } from 'primeng/dialog';
import { forkJoin, of } from 'rxjs';
import { ApiService } from 'src/app/services/api/api.service';
import { AppTopbar } from 'src/app/layout/component/app.topbar';
import { TopbarService } from 'src/app/services/topbar/topbar.service';
import { TopbarAction } from 'src/app/services/topbar/topbar.actions';
import { CurrencySymbolPipe } from "../../services/currency-symbol/currency-symbol.pipe";
import { FormatCreditsPipe } from "../../services/credits-format/credits-format.pipe";
import { ProductPriceService } from 'src/app/services/product-price/product-price.service';
import { getProductDescription, unitProductNature } from 'src/app/utils/product-description.utils';

@Component({
  selector: 'app-offers',
  imports: [RouterModule, DialogModule, SkeletonModule, CurrencySymbolPipe, FormatCreditsPipe],
  standalone: true,
  templateUrl: './offers.component.html',
  styleUrl: './offers.component.scss'
})
export class OffersComponent {
  @ViewChild('topbar') topbar: AppTopbar | undefined;

  displayModal: boolean = true;

  @Input() context: any;
  @Input() is_orga_only: any;
  @Input() isPaymentRequired: boolean = false;
  @Input() isCreditsRequired: boolean = false;
  // Quota d'utilisateurs atteint : afficher l'addon "+1 user"
  // (produit is_orga_only avec nbr_users >= 1, par symétrie avec l'addon
  // "+1 base" identifié via nbr_bases).
  @Input() is_user_addon: boolean = false;
  // Ajout de base SANS offre Equipe : la base s'ajoute via un abonnement
  // mensuel correspondant à son type (les addons "+1 base" sont réservés à
  // l'offre Equipe). base_type = 'classic' | 'encrypted'.
  @Input() is_base_subscription: boolean = false;
  @Input() base_type: string = 'classic';
  // Ajout de base en mode « abonnement individuel par base » : la modale
  // présente les formules (produits is_formule, regroupement des bases) ET
  // les abonnements individuels par base (classique et cryptée).
  @Input() is_add_base: boolean = false;
  // Quota épuisé (base ou utilisateur) avec une offre active : la modale ne
  // présente QUE les compléments de cette nature (+1 base / +1 utilisateur)
  // — produits liés (linked_product_ids) ou legacy is_orga_only —, filtrés
  // par les offres ACTIVES de l'orga, avec toggle Mensuel/Annuel.
  @Input() addon_kind: 'base' | 'user' | null = null;
  // Quota de bases épuisé SANS formule (offre simple) : la modale présente
  // les abonnements individuels par base (l'offre Simple) — la base est
  // créée automatiquement par le back après paiement.
  @Input() is_simple_offer: boolean = false;
  // Orga CIBLE du panier (ajout de base pour une autre orga que l'active,
  // ex. première base d'une orga fraîchement créée). null = orga active.
  @Input() organisationId: any = null;
  @Output() closeModal = new EventEmitter<any>();
  @Output() activeProduct = new EventEmitter<any>();

  // Ids des produits des abonnements ACTIFS de l'orga cible : périmètre des
  // produits liés (mode addon_kind).
  private activeOfferIds: Set<number> = new Set();


  constructor(
    private apiService: ApiService,
    private router: Router,
    private topbarService: TopbarService,
    protected price: ProductPriceService
  ) { }

  // ── Titre selon le contexte d'ouverture ────────────────────────────────
  get title(): string {
    if (this.isCreditsRequired) return 'Rechargez vos NrToks';
    if (this.addon_kind === 'user' || this.is_user_addon) return 'Ajoutez un utilisateur supplémentaire';
    if (this.addon_kind === 'base') return 'Ajoutez une base supplémentaire';
    if (this.is_add_base || this.is_base_subscription || this.is_simple_offer) return 'Ajoutez une base';
    if (this.isPaymentRequired) return 'Activez votre espace MultiBases';
    if (this.is_orga_only) return 'Oops ! Tu n\'as plus assez d\'espace';
    return 'Optez pour une de nos offres';
  }

  // Helper pour vérifier si un prix de réduction existe et est valide (> 0)
  isFirstMonthPriceValid(product: any): boolean {
    return this.price.hasFirstMonthPromo(product);
  }

  hasDescription(product: any): boolean {
    return !!product?.description && product.description !== 'null';
  }

  // Icône de la vignette d'un complément, selon sa nature.
  addonIcon(product: any): string {
    switch (unitProductNature(product)) {
      case 'users': return 'pi pi-users';
      case 'base': return 'pi pi-database';
      default: return 'pi pi-bolt';
    }
  }

  ngOnInit() {

    // Le tunnel de paiement s'ouvre (topbar) : cette modale d'offres doit
    // se fermer, sinon elle recouvre la modale de paiement (z-index).
    this.topbarService.action$.subscribe(({ action }) => {
      if (action === TopbarAction.OpenCheckout) {
        this.displayModal = false;
        this.closeModal.emit();
      }
    });
    this.getProducts()
  }

  allProducts: any[] = [];
  productsToShow: any[] = [];

  // Chargement terminé ? (allProducts vide après fetch = aucune offre)
  get isLoading(): boolean {
    return !!this.allProducts && !this.allProducts.length;
  }

  // ── Classification (même grille que /market) ───────────────────────────

  // Un produit lié (linked_product_ids renseigné) est un complément, même
  // sans is_orga_only (un « +1 utilisateur » lié aux offres Pro/Max).
  private isAddonLinked(p: any): boolean {
    return Array.isArray(p?.linked_product_ids) && p.linked_product_ids.length > 0;
  }

  private isAddonProduct(p: any): boolean {
    return !!p.is_subscription && (p.is_orga_only || this.isAddonLinked(p));
  }

  // Complément « +1 utilisateur » NON marqué (ni is_orga_only ni produits
  // liés) : seul son nom le trahit. On exige le motif « +1 / supplémentaire /
  // additionnel » pour ne pas confondre avec un abonnement par base dont le
  // nom mentionne les utilisateurs (« Base Pro 5 users »). Sans ce repli, le
  // produit apparaît dans les modales d'ajout de base et manque à la modale
  // d'ajout d'utilisateur.
  private isUnmarkedUserAddon(p: any): boolean {
    if (!p?.is_subscription || p.is_formule || p.is_orga_only || this.isAddonLinked(p)) return false;
    if (Number(p?.nbr_bases) > 0) return false;
    const name = (p.name || '').toLowerCase();
    return /\+\s*1|suppl|additionnel/.test(name) && /(utilisateur|user)/.test(name);
  }

  // Nature d'un complément (mode addon_kind) : le compteur porte la nature,
  // avec repli sur le nom du produit.
  private addonMatchesKind(p: any, kind: 'base' | 'user'): boolean {
    const name = (p?.name || '').toLowerCase();
    if (kind === 'user') {
      return Number(p?.nbr_users) > 0 || name.includes('user') || name.includes('utilisateur');
    }
    return Number(p?.nbr_bases) > 0 || name.includes('base');
  }

  // Visibilité d'un produit lié : intersection requise avec les offres
  // ACTIVES de l'orga cible. Sans produits liés (legacy is_orga_only) :
  // visible (ces modales ne s'ouvrent qu'avec une offre active).
  private isProductVisible(p: any): boolean {
    const scope = Array.isArray(p?.linked_product_ids)
      ? p.linked_product_ids.map((id: any) => Number(id))
      : [];
    if (scope.length === 0) return true;
    // Offres actives inconnues (orga non résolue ou chargement en échec) :
    // mode dégradé, on montre le complément — le back validera de toute façon.
    if (!this.activeOfferIds.size) return true;
    return scope.some((id: number) => this.activeOfferIds.has(id));
  }

  // ── Toggle Mensuel / Annuel (comme sur /market) ────────────────────────
  // Cycle : annuel = nbr_month > 1 ; tout le reste (mensuel, cycles en
  // jours…) tombe dans le bucket mensuel pour ne jamais disparaître des
  // deux périodes à la fois.

  period: 'm' | 'a' = 'a';

  setPeriod(period: 'm' | 'a') {
    this.period = period;
  }

  private isAnnualCycle(p: any): boolean {
    return Number(p?.nbr_month) > 1;
  }

  private byPeriod(list: any[]): any[] {
    return list.filter(p =>
      this.period === 'a' ? this.isAnnualCycle(p) : !this.isAnnualCycle(p)
    );
  }

  // Liste soumise au toggle selon le mode d'ouverture.
  private get periodScope(): any[] {
    if (this.is_add_base) {
      return this.productsToShow.filter(p =>
        p.is_subscription && !p.is_orga_only && !this.isAddonLinked(p)
      );
    }
    if (this.addon_kind) return this.productsToShow;
    if (this.is_simple_offer) {
      return this.productsToShow.filter(p => p.is_subscription);
    }
    return [];
  }

  // Le toggle n'a d'intérêt que si la période opposée contient quelque chose.
  get hasBothPeriods(): boolean {
    const items = this.periodScope;
    return items.some(p => this.isAnnualCycle(p))
      && items.some(p => !this.isAnnualCycle(p));
  }

  private normalizeName(name: string): string {
    return (name || '').replace(/\s*\((annuel|annuelle|mensuel|mensuelle|annual|monthly)\)\s*$/i, '').trim();
  }

  // ── Groupes de produits, rendus comme sur /market et le panier ─────────
  get formulaProducts(): any[] {
    const subs = this.productsToShow.filter(p => p.is_subscription);
    // Offre simple (quota de bases épuisé sans formule) : abonnements
    // individuels filtrés par période, cartes « formule ».
    if (this.is_simple_offer) return this.byPeriod(subs);
    // Les modales « compléments » (addon_kind, is_user_addon, is_orga_only)
    // et « abonnement par type de base » (is_base_subscription) n'ont pas de
    // groupe Formule : leurs produits sont déjà rendus par addonProducts —
    // sinon chaque complément y apparaît en doublon, hors toggle de période.
    if (this.addon_kind || this.is_user_addon || this.is_orga_only || this.is_base_subscription) {
      return [];
    }
    // En mode ajout de base, ce groupe ne contient que les formules : les
    // abonnements individuels par base ont leur propre section. Comme sur
    // /market, le toggle Mensuel/Annuel filtre par cycle de facturation
    // (nbr_month), avec un ordre de familles stable entre périodes.
    if (!this.is_add_base) return subs;

    const formules = subs.filter(p => p.is_formule);
    const order = new Map<string, number>();
    formules.forEach(p => {
      const base = this.normalizeName(p.name);
      if (!order.has(base)) order.set(base, order.size);
    });

    return this.byPeriod(formules)
      .sort((a, b) =>
        (order.get(this.normalizeName(a.name)) ?? 0) - (order.get(this.normalizeName(b.name)) ?? 0)
      );
  }

  // Abonnements individuels par base (mode ajout de base uniquement) :
  // abonnements hors formule, la base est créée automatiquement après le
  // paiement. Comme pour les formules, le toggle Mensuel/Annuel filtre par
  // cycle (nbr_month).
  get baseAbosProducts(): any[] {
    if (!this.is_add_base) return [];
    return this.byPeriod(this.productsToShow.filter(p =>
      p.is_subscription && !p.is_orga_only && !p.is_formule
    ));
  }

  get addonProducts(): any[] {
    // Mode quota épuisé : la liste est déjà restreinte aux compléments de la
    // nature demandée, il ne reste que le filtre de période.
    if (this.addon_kind) return this.byPeriod(this.productsToShow);
    return this.productsToShow.filter(p => p.is_orga_only);
  }

  get unitProducts(): any[] {
    return this.productsToShow.filter(p => !p.is_subscription && !p.is_orga_only);
  }

  // Le type « cryptée » se reconnaît à enabled_features contenant
  // pii_anonymization_enabled. Le champ n'est pas toujours un tableau selon
  // les produits (objet/chaîne selon le back) : on garde Array.isArray.
  isEncryptedBaseProduct(product: any): boolean {
    return (
      Array.isArray(product?.enabled_features) &&
      product.enabled_features.includes('pii_anonymization_enabled')
    );
  }

  getProducts() {
    // Mode compléments (addon_kind) : la visibilité des produits liés
    // dépend des offres ACTIVES de l'orga cible — on les charge d'abord.
    const orgId = this.organisationId ?? this.context?.organisation?.id;
    const needActiveIds = !!this.addon_kind && !!orgId && orgId !== 'none';

    forkJoin({
      products: this.apiService.getProducts(),
      activeIds: needActiveIds
        ? this.apiService.activeOrgProductIds(orgId)
        : of([] as number[]),
    }).subscribe(({ products, activeIds }: any) => {
      this.allProducts = products;
      this.activeOfferIds = new Set((activeIds || []).map((id: any) => Number(id)));

      // Cas spécial : organisation qui doit payer (has_to_pay = true)
      if (this.isPaymentRequired) {
        this.productsToShow = this.allProducts.filter(p =>
          p.is_new_organisation && p.is_formule && p.is_subscription
        );
      }
      // Cas spécial : base qui n'a plus de crédits
      else if (this.isCreditsRequired) {
        this.productsToShow = this.allProducts.filter(p =>
          !p.is_formule && !p.is_subscription && !p.is_orga_only
        );
      }
      // Quota épuisé (base ou utilisateur) avec offre active : uniquement les
      // compléments de cette nature, liés aux offres actives de l'orga — ou
      // non marqués mais reconnaissables à leur nom (« +1 utilisateur »).
      else if (this.addon_kind) {
        this.productsToShow = this.allProducts.filter(p =>
          (this.isAddonProduct(p) || this.isUnmarkedUserAddon(p)) &&
          this.addonMatchesKind(p, this.addon_kind!) &&
          this.isProductVisible(p)
        );
      }
      // Quota de bases épuisé SANS formule : l'offre Simple (abonnements
      // individuels par base, mensuel et annuel — la base est créée par le
      // back après paiement).
      else if (this.is_simple_offer) {
        this.productsToShow = this.allProducts.filter(p =>
          p.is_subscription && !p.is_formule && !p.is_new_organisation &&
          !p.is_orga_only && !this.isAddonLinked(p) && !this.isUnmarkedUserAddon(p)
        );
      }
      // Quota d'utilisateurs atteint (legacy) : addon "+1 user" (is_orga_only
      // avec nbr_users >= 1). On exclut ainsi les addons "+1 base".
      else if (this.is_user_addon) {
        this.productsToShow = this.allProducts.filter(
          p => p.is_orga_only && p.nbr_users != null && p.nbr_users >= 1,
        );
      }
      // Ajout de base en mode « abonnement individuel par base » : deux
      // options — les formules (regroupement, souvent plus rentables à
      // partir de plusieurs bases) et les abonnements individuels (classique
      // + cryptée, mensuels ET annuels — le toggle filtre par période, la
      // base est créée automatiquement après paiement).
      else if (this.is_add_base) {
        this.productsToShow = this.allProducts.filter(p =>
          p.is_subscription && !p.is_orga_only && !this.isAddonLinked(p) &&
          !this.isUnmarkedUserAddon(p) && (
            p.is_formule ||
            (!p.is_formule && !p.is_new_organisation)
          )
        );
      }
      // Ajout de base sans offre Equipe : abonnements MENSUELS (hors Equipe
      // is_new_organisation et hors addons is_orga_only) correspondant au
      // type de base choisi. Le type « cryptée » se reconnaît à
      // enabled_features contenant pii_anonymization_enabled.
      else if (this.is_base_subscription) {
        const wantsEncrypted = this.base_type === 'encrypted';
        this.productsToShow = this.allProducts.filter(p =>
          p.is_subscription && !p.is_orga_only && !p.is_new_organisation &&
          !this.isUnmarkedUserAddon(p) &&
          Number(p.nbr_month) === 1 &&
          (this.isEncryptedBaseProduct(p) === wantsEncrypted)
        );
      }
      // Limite de bases atteinte (legacy) : addon "+1 base" (is_orga_only
      // avec nbr_bases >= 1). On exclut ainsi les addons "+1 user".
      else if (this.is_orga_only) {
        this.productsToShow = this.allProducts.filter(
          p => p.is_orga_only && p.nbr_bases != null && p.nbr_bases >= 1,
        );
      } else {
        // Modale générique : les formules ne sont proposées que tant que la
        // base active est en démo (pas encore d'abonnement). Une fois la base
        // active : l'ajout de bases passe par un abonnement mensuel dédié
        // (sans offre Equipe) ou par les compléments « +1 base » (avec).
        this.productsToShow = this.context.base.is_demo
          ? this.allProducts.filter(p => p.is_subscription && !p.is_orga_only)
          : [];
      }

      // description : objet { "<clé>": "<texte>" } → on résout la variante
      // « market » (fallback default) une fois pour tous les templates.
      this.allProducts.forEach(product => {
        product.description = getProductDescription(product, 'market');
      });

      // Comme sur /market : bascule le toggle si une période est vide, pour
      // ne jamais ouvrir la modale sur une grille vide.
      const scope = this.periodScope;
      if (scope.length) {
        if (!scope.some(p => this.isAnnualCycle(p))) this.period = 'm';
        else if (!scope.some(p => !this.isAnnualCycle(p))) this.period = 'a';
      }
    })
  }

  // Libellé du CTA selon le type de produit (même convention que /market)
  ctaLabel(product: any, type: 'formula' | 'addon' | 'unit'): string {
    if (product.added) return type === 'unit' ? 'Au panier' : 'Accéder au panier';
    if (type === 'addon') return this.addonCtaLabel(product);
    if (type === 'unit') return 'Ajouter au panier';
    if (this.is_add_base && product.is_formule) return 'Passer sur cette formule';
    if (this.is_add_base || this.is_base_subscription) return 'Ajouter une base';
    return 'Profiter de l\'offre';
  }

  addonCtaLabel(product: any): string {
    const name = (product?.name || '').toLowerCase();
    if (name.includes('user') || name.includes('utilisateur')) return 'Ajouter un utilisateur';
    if (name.includes('base')) return 'Ajouter une base';
    return 'Ajouter au panier';
  }

  handleProductClick(product: any) {
    if (product.added) {
      this.topbarService.trigger(TopbarAction.OpenBasket);      // 👉 Accéder au panier
    } else {
      product.added = true;
      this.topbarService.trigger(TopbarAction.AddProductToBasket, {
        product: product,
        organisation_id: this.organisationId,
      }); // 👉 Ajouter le produit au panier
    }
  }


  close() {
    this.displayModal = false;
    this.closeModal.emit();
  }

  openModuleInfo(url: any) {
    window.open(url, '_blank');
  }

  openMultiBasesInfo() {
    window.open('https://narratheque.io/info-offre-multibases', '_blank');
  }


}
